import { createFileRoute, notFound } from "@tanstack/react-router";
import authStyles from "../styles/auth.css?url";
import bibleStyles from "../styles/bible.css?url";
import matchupStyles from "../styles/matchups.css?url";
import communityStyles from "../styles/community.css?url";
import membershipStyles from "../styles/membership.css?url";
import servicesStyles from "../styles/services.css?url";
import { CreatorHome } from "../components/home/creator-home";
import { homeStylesheets } from "../components/home/stylesheets";
import { BuiltInContent } from "../components/built-in-content";
import { DocumentContent } from "../components/document-content";
import { EditorCanvasSeed } from "../components/editor-canvas-seed";
import { NativePending } from "../components/native-pending";
import { MatchupGuide } from "../components/matchups/guide";
import { walkPageNodes } from "../../../shared/src/page-tree";
import { useShell, withDocumentStyles } from "../lib/document-route";
import { loadPreviewPage } from "../server/preview-page";

/**
 * The editor's canvas and previews (server/preview.server.ts renders this route for an authorized
 * POST /preview/customization). It is never indexed, kept or opened directly.
 */
export const Route = createFileRoute("/preview/customization")({
  loader: async () => {
    const data = await loadPreviewPage();
    if (data.kind === "missing" || data.kind === "redirect") throw notFound();
    return data;
  },
  head: ({ loaderData }) =>
    withDocumentStyles(
      {
        // A composed feature page's built-in content brings its feature's styles, as its route does.
        links: builtInStyles(loaderData),
        meta: [
          {
            title:
              loaderData?.kind === "page"
                ? loaderData.page.seoTitle || loaderData.page.title
                : "Preview",
          },
          { name: "robots", content: "noindex,nofollow" },
        ],
      },
      loaderData?.kind === "page" ? loaderData.document : null,
    ),
  component: Preview,
});

function builtInStyles(data: Awaited<ReturnType<typeof loadPreviewPage>> | undefined) {
  if (data && "guide" in data && data.guide)
    return [{ rel: "stylesheet", href: matchupStyles, precedence: "matchups" }];
  const page = data && "builtIn" in data ? data.builtIn?.page : undefined;
  if (page === "bible") return [{ rel: "stylesheet", href: bibleStyles }];
  if (page === "join") return [{ rel: "stylesheet", href: membershipStyles }];
  if (page === "sign-in" || page === "sign-up") return [{ rel: "stylesheet", href: authStyles }];
  if (page === "community") return [{ rel: "stylesheet", href: communityStyles }];
  if (page === "coaching" || page === "booking" || page === "tournaments")
    return [{ rel: "stylesheet", href: servicesStyles }];
  if (data?.kind === "home")
    return homeStylesheets(data.home).map((href) => ({ rel: "stylesheet", href }));
  if (page === "matchups" || page === "live")
    return [{ rel: "stylesheet", href: matchupStyles, precedence: "matchups" }];
  return [];
}

const ignore = () => {};

function Preview() {
  const data = Route.useLoaderData();
  const shell = useShell();
  return data.kind === "page" ? (
    <>
      {data.document && data.bindings && (
        <EditorCanvasSeed
          value={{
            version: 1,
            props: {
              bindings: data.bindings,
              experiences: shell.experiences,
              creator: data.creator,
              member: data.member,
              creatorId: shell.creator.id,
              images: data.images,
              path: data.page.path,
            },
          }}
        />
      )}
      <DocumentContent
        data={data}
        shell={shell}
        // The owner's Website view in their dashboard is their own site, not the editor's canvas.
        canvas={!shell.ownerWebsiteView}
        builtIn={
          "guide" in data && data.guide ? (
            <GuidePreview data={data} guide={data.guide} />
          ) : "builtIn" in data && data.builtIn ? (
            <BuiltInContent data={data.builtIn} />
          ) : undefined
        }
      />
    </>
  ) : data.kind === "home" ? (
    <CreatorHome home={data.home} notice={null} error={false} />
  ) : (
    <NativePending name="Home" />
  );
}

type PreviewData = Awaited<ReturnType<typeof loadPreviewPage>>;

/** A guide template's guide block, with the guide picked in the editor, as visitors see it. */
function GuidePreview({
  data,
  guide,
}: {
  data: Extract<PreviewData, { kind: "page" }>;
  guide: NonNullable<Extract<PreviewData, { guide: unknown }>["guide"]>;
}) {
  const block = data.document
    ? walkPageNodes(data.document).find((node) => node.kind === "matchupGuide")
    : undefined;
  return (
    <MatchupGuide
      guide={guide.guide}
      library={guide.library}
      libraryHref="/matchups"
      view="simple"
      hero={block?.kind === "matchupGuide" ? block.hero : true}
      toolbar={block?.kind === "matchupGuide" ? block.toolbar : true}
      onOpen={ignore}
    />
  );
}
